import "vuetify/styles";
import { createVuetify, IconProps } from "vuetify";
import { aliases } from "vuetify/iconsets/mdi-svg";
import style from "@/scss/variables.module.scss";
import * as labsComponents from "vuetify/labs/components";
import * as mdiJs from "@mdi/js";
import { h } from "vue";

// Convert MDI icons to kebab-case because that's how most icons
// are named.
const mdiIconMap = Object.fromEntries(
	Object.entries(mdiJs).map(([key, value]) => [
		key.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase(),
		value,
	])
);

const iconShim = {
	component: (props: IconProps) => {
		const iconName = typeof props.icon === "string" ? props.icon : "";
		let path = "";

		// Handle $prefix aliases
		if (iconName.startsWith("$")) {
			path =
				aliases[iconName.substring(1)] ||
				mdiIconMap[iconName.substring(1)] ||
				"";
		} else {
			// Handle regular and mdi- prefixed names
			const baseName = iconName.startsWith("mdi-")
				? iconName.substring(4)
				: iconName;
			path =
				mdiIconMap[baseName] ||
				mdiIconMap["mdi-" + baseName] ||
				mdiIconMap[iconName] ||
				"";
		}

		if (!path) {
			/**
			 * NOTE(Leon): Ok, so what is this for you might ask? This whole thing is a bit ඞ
			 * Basically it's a fallback for font awesome. Even tho it's meant for mdi.
			 *
			 * We *ideally* should really only use 1 icon pack and not 2 but alas.
			 *
			 * But under the hood vuetify does basically just this, and the FA npm package
			 * does the rest. Because we want to also mix and match mdi + fa using v-icon
			 * this function handles both.
			 */
			return h(props.tag, { class: props.icon }, null);
		}

		return h("span", {}, [
			h(
				"svg",
				{
					class: "v-icon__svg",
					xmlns: "http://www.w3.org/2000/svg",
					viewBox: "0 0 24 24",
					role: "img",
					"aria-hidden": "true",
				},
				[h("path", { d: path })]
			),
		]);
	},
};

/*
 * These colors are defined in variables.module.scss
 */
const themes = {
	BLUE_THEME: {
		name: "BLUE_THEME",
		dark: false,
		colors: {
			"on-surface": style["color-on-surface"],
			"blue-accent": style["color-blue-accent"],

			background: style["color-background"],
			surface: style["color-surface"],
			primary: style["color-primary"],
			secondary: style["color-secondary"],
			"secondary-tonal": style["color-secondary-tonal"],

			info: style["color-info"],
			success: style["color-success"],
			warning: style["color-warning"],
			error: style["color-error"],

			active: style["color-active"],
			"active-secondary": style["color-active-secondary"],
			inactive: style["color-inactive"],

			"on-background": style["color-on-background"],
		},
	},
};

const inputFieldDefaultProps = {
	variant: "filled",
	density: "comfortable",
	bgColor: "background",
};

export default createVuetify({
	// blueprint: md2, <-- Had to remove this to make the pie work, guess it's not supported in since the pie is beta.. also not sure we need the blueprint.
	components: { ...labsComponents },
	icons: {
		defaultSet: "mdi",
		aliases,
		sets: {
			mdi: iconShim,
		},
	},
	theme: {
		defaultTheme: "BLUE_THEME",
		themes,
	},
	defaults: {
		VCard: { rounded: "md", variant: "elevated" },
		VChip: { label: true, variant: "tonal" },
		VBtn: { color: "primary", variant: "flat" },
		VCardTitle: { class: "py-3" },
		VTextField: {
			...inputFieldDefaultProps,
			validateOn: "invalid-input",
		},
		VTextarea: inputFieldDefaultProps,
		VSelect: inputFieldDefaultProps,
		VAutocomplete: inputFieldDefaultProps,
		VNumberInput: {
			...inputFieldDefaultProps,
			inset: true,
			controlVariant: "stacked",
		},
		VCombobox: inputFieldDefaultProps,
		VListItem: { minHeight: "45px", colors: style["color-secondary"] },
		VList: { colors: style["color-secondary"] },
		VTooltip: { location: "top", openDelay: 200, closeDelay: 100 },
		VAlert: {
			density: "comfortable",
			type: "info",
			variant: "tonal",
			border: "start",
			class: ["pl-4"],
		},
		VMenu: { variant: "tonal" },
		VBtnGroup: { color: "secondary", bgColor: "surface" },
		VTabs: {
			style: `background-color: ${style["color-secondary"]}; color: #535A66 !important;`,
		},
		VToolbar: {
			style: `background-color: ${style["color-secondary"]}`,
		},
		VExpansionPanelTitle: {
			color: "secondary",
			rounded: false,
		},
		VExpansionPanel: { elevation: 0, rounded: false },
		VCheckbox: { color: "primary" },
	},
});
